import { type MarketingTracking, type TrackingKey } from "@types";

const storageKey = "marketingTracking";

const urlKeyToTrackingKey = {
  gclid: "gclid",
  fbclid: "fbclid",
  msclkid: "msclkid",
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  utm_term: "utmTerm",
  utm_content: "utmContent",
} as const satisfies Record<string, TrackingKey>;

type UrlKey = keyof typeof urlKeyToTrackingKey;

const trackingKeys = Object.values(urlKeyToTrackingKey) as readonly TrackingKey[];
const maxValueLength = 255;

const getStoredMarketingTracking = (): MarketingTracking | null => {
  try {
    const raw = globalThis.sessionStorage?.getItem(storageKey);
    if (raw == null) {
      return null;
    }
    const parsed = JSON.parse(raw) as unknown;
    if (parsed == null || typeof parsed !== "object") {
      return null;
    }
    const out: MarketingTracking = {};
    for (const key of trackingKeys) {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === "string" && value.length > 0) {
        out[key] = value;
      }
    }
    return Object.keys(out).length === 0 ? null : out;
  } catch {
    return null;
  }
};

const writeStored = (value: MarketingTracking): void => {
  try {
    globalThis.sessionStorage?.setItem(storageKey, JSON.stringify(value));
  } catch {
    // ignore quota / availability errors — tracking is best-effort
  }
};

const captureMarketingTrackingFromUrl = (search: string): void => {
  const parameters = new URLSearchParams(search);
  const incoming: MarketingTracking = {};

  for (const [urlKey, trackingKey] of Object.entries(urlKeyToTrackingKey) as [UrlKey, TrackingKey][]) {
    const value = parameters.get(urlKey);
    if (value != null && value.length > 0 && value.length <= maxValueLength) {
      incoming[trackingKey] = value;
    }
  }

  if (Object.keys(incoming).length === 0) {
    return;
  }

  writeStored({ ...getStoredMarketingTracking(), ...incoming });
};

export { getStoredMarketingTracking, captureMarketingTrackingFromUrl };
